/**
 * Segunda inspección a 0 € (#907, ACEL-Cc).
 *
 * Mismo cálculo que `SegundaACeroService.precioSegunda` de la API, que es con el que se crea la línea
 * de segunda: un % a 0 no tapa un importe fijo, que es lo que deja el calculador de tarifas cuando la
 * tarifa da precio de segunda y no porcentaje. Si los dos lados calcularan distinto, el aviso saldría
 * donde la API deja pasar o al revés.
 */
const num = function (v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

export const precioSegunda = function (linea) {
  if (!linea) return 0;
  const porc = num(linea.porcSegunda);
  if (porc > 0) return num(linea.precioUnitario) * porc / 100;
  const importe = num(linea.importeSegunda);
  return importe > 0 ? importe : 0;
};

// Las mismas que se quedan fuera al planificar: no son una inspección y no tienen segunda.
const SIN_SEGUNDA = ['TASAS', 'SUBCONTRATACION', 'COMISION'];

export const tieneSegunda = function (linea) {
  return !!linea && SIN_SEGUNDA.indexOf(linea.tipoLinea) < 0;
};

/** El descuento de la línea, que la segunda hereda al crearse: con un 100 % sale gratis igualmente. */
export const descuentoTotal = function (linea) {
  return !!linea && num(linea.porcDescuento) >= 100;
};

// A céntimos y con el medio hacia fuera, como el HALF_UP de la API. Se pasa por el texto del número
// para que 49.995 no se quede en 49.99 por cómo guarda los decimales la coma flotante.
const centimos = function (v) {
  if (!(Math.abs(v) >= 0.000001)) return 0;
  return Math.sign(v) * Number(Math.round(Number(Math.abs(v) + 'e2')) + 'e-2');
};

/**
 * Lo que cobra de verdad una segunda: la cuenta de `LineaEMO.importeConDescuento` de la API, que es la
 * que guarda la línea. El bruto y el descuento se redondean a céntimos y después se restan; sin ese
 * redondeo, 50 € al 99,99 % darían 0,01 € aquí y 0,00 € en la línea.
 */
export const netoSegunda = function (linea) {
  const antes = centimos(precioSegunda(linea));
  return centimos(antes - centimos(antes * num(linea && linea.porcDescuento) / 100));
};

/**
 * Por qué la segunda de la línea sale a 0 €, o '' si no sale a 0 € o la línea no tiene segunda.
 * Mismos casos que `SegundaACeroService.motivo` de la API.
 */
export const motivoACero = function (linea) {
  if (!tieneSegunda(linea)) return '';
  if (centimos(precioSegunda(linea)) <= 0) {
    if (!(num(linea.porcSegunda) > 0) && !(num(linea.importeSegunda) > 0)) return 'No tiene porcentaje ni importe de segunda';
    return num(linea.porcSegunda) > 0 && !(num(linea.precioUnitario) > 0)
      ? 'Su % de segunda se calcula sobre una primera a 0 €, así que también da 0 €'
      : 'Su precio de segunda no llega a un céntimo';
  }
  if (num(linea.porcDescuento) > 0 && netoSegunda(linea) <= 0) {
    return 'Tiene un descuento del ' + num(linea.porcDescuento) + ' %, que se aplica también a la segunda y la deja a 0 €';
  }
  return '';
};

export const saleACero = function (linea) {
  return !!motivoACero(linea);
};

/**
 * El precio que se propone en el aviso: el de la tarifa con la que se calculó la línea, si lo tiene;
 * si no, el % de segunda del servicio. Sin ninguno de los dos, no se propone nada.
 */
export const propuestaDe = function (linea) {
  const t = linea && linea.tarifaCalculo;
  if (t && num(t.porcSegunda) > 0) {
    return { porcSegunda: num(t.porcSegunda), origen: 'tarifa' + (t.tarifaCodigo ? ' ' + t.tarifaCodigo : '') };
  }
  if (t && num(t.precio2a) > 0) {
    return { importeSegunda: num(t.precio2a), origen: 'tarifa' + (t.tarifaCodigo ? ' ' + t.tarifaCodigo : '') };
  }
  const s = linea && linea.servicio;
  if (s && num(s.porcSegunda) > 0) {
    return { porcSegunda: num(s.porcSegunda), origen: 'servicio' };
  }
  return null;
};

/** Lo que devuelve la API cuando una segunda saldría a 0 € (ver `SegundaACeroService`). */
export const esErrorSegundaACero = function (e) {
  const msg = e && (typeof e === 'string' ? e : e.message);
  return !!msg && msg.indexOf('sale a 0 €') >= 0;
};

/**
 * #1142 · Lo que la API necesita de la línea para pedir que su segunda se deje a 0 €: identificarla,
 * volver a comprobar que sale a 0 € con su misma cuenta y dar a quien decide el importe de referencia.
 * Solo eso, y no la línea entera del formulario, que arrastra documentos y expedientes.
 */
export const lineaParaPedir = function (linea) {
  const l = linea || {};
  const t = l.tarifaCalculo;
  const s = l.servicio;
  const sc = l.servicioCompleto;
  return {
    numero: l.numero,
    claveSegundaACero: l.claveSegundaACero,
    tipoLinea: l.tipoLinea,
    servicio: s ? { codigo: s.codigo, nombre: s.nombre || s.userText || s.codigo } : null,
    servicioCompleto: sc ? { porcSegunda: sc.porcSegunda } : (s && s.porcSegunda ? { porcSegunda: s.porcSegunda } : null),
    cantidad: l.cantidad,
    precioUnitario: l.precioUnitario,
    porcSegunda: l.porcSegunda,
    importeSegunda: l.importeSegunda,
    porcDescuento: l.porcDescuento,
    tarifaCalculo: t ? { porcSegunda: t.porcSegunda, precio2a: t.precio2a } : null
  };
};

const mismo = function (x, y) {
  return x !== undefined && x !== null && y !== undefined && y !== null && String(x) === String(y);
};

/**
 * #1142 · Si la línea trae su segunda autorizada a 0 € para su servicio. Lo apunta la API al guardar,
 * desde la cola; aquí solo sirve para no volver a preguntar ni a pedir lo que ya está autorizado: la
 * API lo vuelve a comprobar.
 *
 * Y solo si es de esta misma línea —o la que heredó esta línea del pedido de su oferta—: una línea
 * copiada de otra, como la nueva primera tras un negativo, puede traer la de aquella. Con `contexto`
 * ({ documento, id }) se mira además que sea de este documento.
 */
export const autorizadaEnLinea = function (linea, contexto) {
  const a = linea && linea.segundaACeroAutorizada;
  if (!a || !a.idSolicitud) return false;
  const servicio = linea.servicio ? linea.servicio.codigo : null;
  if ((a.servicio || null) !== (servicio || null)) return false;
  // La clave dice de qué línea es; la API aplica la misma regla al guardar.
  if ((a.claveLinea || null) !== (linea.claveSegundaACero || null)) return false;
  if (a.numeroLineaPedido !== undefined && a.numeroLineaPedido !== null) {
    return mismo(a.numeroLineaPedido, linea.numero) && (!contexto || contexto.documento === 'PEDIDO');
  }
  return mismo(a.numeroLinea, linea.numero)
    && (!contexto || (a.documento === contexto.documento && mismo(a.idDocumento, contexto.id)));
};

const dosCifras = function (n) {
  return (n < 10 ? '0' : '') + n;
};

/** «01/11/2026 11:28», con la hora del navegador; '' si no hay fecha o no se entiende. */
export const fechaYHora = function (valor) {
  const d = valor ? new Date(valor) : null;
  if (!d || isNaN(d.getTime())) return '';
  return dosCifras(d.getDate()) + '/' + dosCifras(d.getMonth() + 1) + '/' + d.getFullYear() + ' '
    + dosCifras(d.getHours()) + ':' + dosCifras(d.getMinutes());
};

const quienYCuando = function (a) {
  const fecha = fechaYHora(a.fechaAutorizacion);
  return 'la autorizó ' + (a.nombreAutorizador || a.autorizadaPor || 'quien la resolvió') + (fecha ? ' el ' + fecha : '')
    + ' en OEE › Autorizaciones' + (a.nombreSolicitante ? ' (pedida por ' + a.nombreSolicitante + (a.motivo ? ': «' + a.motivo + '»' : '') + ')' : '') + '.';
};

/** «Segunda a 0 €: la autorizó Andrea Bajo el 01/11/2026 11:28 en OEE › Autorizaciones (pedida por …)», o ''. */
export const textoAutorizada = function (linea) {
  if (!autorizadaEnLinea(linea)) return '';
  return 'Segunda a 0 €: ' + quienYCuando(linea.segundaACeroAutorizada);
};

/**
 * #1142 · Dónde está, para una línea del pedido, la autorización por la que va a 0 € su segunda —o
 * ella misma, si es la línea de segunda—: { indice, campo, autorizacion, esSegunda }, o null.
 *
 * - La primera, en su `segundaACeroAutorizada`, si es suya (autorizadaEnLinea).
 * - La línea de segunda, en su `autorizacionACero`, que pone la API al crearla a 0 €.
 * - Una línea de segunda creada antes de que la API lo guardase en ella (la prueba en preview-7):
 *   la de su primera, si esta es su línea de segunda y va a 0 €. Así el pedido ya probado lo dice.
 *
 * `indice` y `campo` son la ruta en el pedido (`lineas.<indice>.<campo>`), que es por donde «Ver
 * Histórico» encuentra el guardado en el que quedó apuntado.
 */
export const autorizacionACeroDe = function (linea, lineas) {
  if (!linea) return null;
  const todas = lineas || [];
  const indiceDe = function (l) {
    const i = todas.indexOf(l);
    return i >= 0 ? i : todas.findIndex(x => x && mismo(x.numero, l.numero));
  };
  if (autorizadaEnLinea(linea)) {
    return { indice: indiceDe(linea), campo: 'segundaACeroAutorizada', autorizacion: linea.segundaACeroAutorizada, esSegunda: false };
  }
  if (linea.autorizacionACero && linea.autorizacionACero.idSolicitud) {
    return { indice: indiceDe(linea), campo: 'autorizacionACero', autorizacion: linea.autorizacionACero, esSegunda: true };
  }
  if (linea.numeroLinea1as === undefined || linea.numeroLinea1as === null || Number(linea.precioUnitario || 0) !== 0) {
    return null;
  }
  const primera = todas.find(l => l && mismo(l.numero, linea.numeroLinea1as) && mismo(l.numeroLineaSegundas, linea.numero));
  if (!primera || !autorizadaEnLinea(primera)) return null;
  return { indice: indiceDe(primera), campo: 'segundaACeroAutorizada', autorizacion: primera.segundaACeroAutorizada, esSegunda: true };
};

/** La frase para la línea: si es la de segunda, «Esta segunda va a 0 €…»; si es la primera, «Su segunda…». */
export const textoAutorizacionACero = function (info) {
  if (!info || !info.autorizacion) return '';
  return (info.esSegunda ? 'Esta segunda va a 0 €: ' : 'Su segunda va a 0 €: ') + quienYCuando(info.autorizacion);
};

export default { autorizadaEnLinea, textoAutorizada, autorizacionACeroDe, textoAutorizacionACero, fechaYHora, precioSegunda, tieneSegunda, descuentoTotal, netoSegunda, motivoACero, saleACero, propuestaDe, esErrorSegundaACero, lineaParaPedir };
